import { useState, useMemo, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { typography, color, Typography, Modal } from '@uniquegood/realworld-studio-design';
import axios, { AxiosResponse } from 'axios';
import { useModalState, useRequest, useRequestImmutable } from '@/hooks';
import { coreApi } from '@/api';
import PageHeader from './components/PageHeader';
import ChannelItem from './components/ChannelItem';
import { Channel, CheckoutResponse } from './types';
import { BILLING_KEY_DOMAIN, PRO_PERIOD_MAP } from '@/constants/pricing';
import { ClickEventName, PageViewEventName, track } from '@/track';

const PLAN_NAME_MAP: Record<string, string> = {
  Pro: '프로',
  Enterprise: '엔터프라이즈',
  Class: '클래스',
  Teacher: '티쳐'
};

// RealWorldApp을 Channel 타입으로 변환
function convertAppToChannel(app: ApplicationResponseModel, currentUserId?: string): Channel {
  const isOwner = app.members?.some(
    (member) => member.id && member.id === currentUserId && member.level === 'Owner'
  );

  return {
    id: app.id ?? '',
    name: app.name ?? '',
    description: app.description ?? '',
    avatarUrl: app.imageUrl,
    isOwner: isOwner ?? false,
    plan: app.planType,
    usePurposeType: app.usePurposeType ?? null
  };
}
const viewEvent = () => {
  track.onPageView({ pageViewEventName: PageViewEventName.view_plan_pro_channel_select });
};

const SelectChannel = () => {
  const { planType = 'pro' } = useParams<{ planType: string; billingPeriod: string }>();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const billingPeriod = searchParams.get('billingPeriod');

  viewEvent();

  if (!billingPeriod) {
    throw new Error('월간, 연간 중 하나를 선택하세요.');
  }

  const history = useHistory();
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: { data: apps } = {}, error: appsError } = useRequestImmutable<
    CommonResponseModel<ApplicationResponseModel[]>
  >('/apps', undefined, {
    headers: {
      'x-rwd-api-version': '1.1'
    }
  });
  const { data: me, error: meError } = useRequest<MyInfo>('/api/me');

  const planName = PLAN_NAME_MAP[planType] || '프로';

  const { modal, openModal, closeModal } = useModalState();

  // apps 데이터를 Channel 타입으로 변환
  const channels = useMemo(() => {
    if (!apps || !me) return [];
    return apps.map((app) => convertAppToChannel(app, me.id));
  }, [apps, me]);

  const isLoading = !apps || !me;

  const handleSelectChannel = (channelId: string) => {
    setSelectedChannelId(channelId);
  };

  const openPaymentPage = useCallback(() => {
    const accessToken = localStorage.getItem('accessToken');

    window.open(
      `${BILLING_KEY_DOMAIN}/billing?accessToken=${accessToken}&mode=register&customerEmail=${me?.email}&customerName=${me?.name}&applicationId=${selectedChannelId}&userId=${me?.id}`,
      'billing-popup',
      'width=800,height=800'
    );
  }, [me?.email, me?.name, me?.id, selectedChannelId]);

  const openPaymentFailModal = () => {
    openModal({
      title: '프로 요금제 결제에 실패했어요',
      children: (
        <Typography type="body">
          결제 실패가 반복되면 1:1 문의하기 버튼을 통해 문의글을 남겨 주세요.
        </Typography>
      ),
      primaryAction: {
        content: '다시 결제하기',
        onAction: () => {
          openPaymentPage();
          closeModal();
        }
      },
      secondaryAction: {
        content: '1:1 문의하기',
        onAction: () => {
          if (ChannelIO) {
            ChannelIO('showMessenger');
          }
        }
      }
    });
  };

  const handleSubscribeResponse = (response: AxiosResponse) => {
    const { status } = response;

    if (status === 200) {
      history.push({
        pathname: `/apps/${selectedChannelId}`,
        state: { proSubscribeSuccess: true }
      });
    } else {
      openPaymentFailModal();
    }
  };

  const handleBillingMessage = async (event: MessageEvent) => {
    // 보안: origin 검증 (결제 페이지 도메인만 허용)
    if (event.origin !== BILLING_KEY_DOMAIN) {
      return;
    }

    const { success } = event.data;

    // 빌링키 등록 성공 시
    if (success) {
      // 빌링키로 요금제 구매 API 호출
      try {
        await coreApi.post(`/api/applications/${selectedChannelId}/plans/subscribe`, {
          productType: PRO_PERIOD_MAP[billingPeriod]
        });

        history.push({
          pathname: `/apps/${selectedChannelId}`,
          state: { proSubscribeSuccess: true }
        });
      } catch (error) {
        console.log('구독 처리 오류 error', error);
        setSubmitError('구독 처리 중 오류가 발생했습니다.');
      }
    } else {
      openPaymentFailModal();
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleBillingMessage);

    return () => {
      window.removeEventListener('message', handleBillingMessage);
    };
  }, [selectedChannelId, planType]);

  const handleSubmit = async () => {
    if (!selectedChannelId || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);
    track.onClick({
      clickEventName: ClickEventName.click_plan_pro_channel_select,
      params: {
        channel_id: selectedChannelId,
        channel_name: channels.find((channel) => channel.id === selectedChannelId)?.name ?? '',
        use_purpose_type:
          channels.find((channel) => channel.id === selectedChannelId)?.usePurposeType ?? ''
      }
    });
    try {
      const response = await coreApi.post<CheckoutResponse>(
        `/api/applications/${selectedChannelId}/plans/checkout/verify`
      );

      const checkoutResponse = response.data;
      const hasBillingKey = !response.data.success;

      if (!hasBillingKey) {
        openPaymentPage();
        return;
      }

      if (!checkoutResponse) {
        throw new Error('서버 응답이 없습니다.');
      }
    } catch (error) {
      setIsSubmitting(false);

      let errorMessage = '결제를 시작하는 중 오류가 발생했습니다.';
      let errorTitle = '';

      if (axios.isAxiosError(error)) {
        const response = error.response as
          | { data?: { message?: string; errorTitle?: string } }
          | undefined;
        errorMessage = response?.data?.message || errorMessage;
        errorTitle = response?.data?.errorTitle || '';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else if (error && typeof error === 'object' && 'errorTitle' in error) {
        errorTitle = (error as unknown as { errorTitle: string }).errorTitle;
      } else {
        errorTitle = '알 수 없는 오류가 발생했습니다.';
      }

      if (errorTitle === 'AlreadyExistsApplicationBillingMethod') {
        const subscribeResponse = await coreApi.post(
          `/api/applications/${selectedChannelId}/plans/subscribe`,
          {
            productType: PRO_PERIOD_MAP[billingPeriod]
          }
        );

        handleSubscribeResponse(subscribeResponse);
        return;
      }

      setSubmitError(errorMessage);
    }
  };

  const selectableChannels = channels.filter(
    (channel) => channel.isOwner && channel.plan === 'Free'
  );

  const hasSelectableChannels = selectableChannels.length > 0;

  if (isLoading) {
    return (
      <PageContainer>
        <ContentWrapper>
          <LoadingText>채널 목록을 불러오는 중...</LoadingText>
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (appsError || meError) {
    return (
      <PageContainer>
        <ContentWrapper>
          <EmptyMessage>
            데이터를 불러오는 중 오류가 발생했습니다. 새로고침 후 다시 시도해주세요.
          </EmptyMessage>
        </ContentWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Modal {...modal} />
      <ContentWrapper>
        <PageHeader planName={planName} userName={me?.name || ''} />

        <FormWrapper>
          <ChannelList>
            {channels.map((channel) => (
              <ChannelItem
                key={channel.id}
                channel={channel}
                isSelected={selectedChannelId === channel.id}
                onSelect={handleSelectChannel}
              />
            ))}
          </ChannelList>

          {!hasSelectableChannels && (
            <EmptyMessage>
              선택 가능한 채널이 없습니다. 새로운 채널을 생성하거나 채널 소유자 권한을 확인해주세요.
            </EmptyMessage>
          )}

          {submitError && <ErrorMessage>{submitError}</ErrorMessage>}

          <SubmitButton onClick={handleSubmit} disabled={!selectedChannelId || isSubmitting}>
            {isSubmitting ? '결제 페이지로 이동 중...' : '채널 선택하기'}
          </SubmitButton>
        </FormWrapper>
      </ContentWrapper>
    </PageContainer>
  );
};

export default SelectChannel;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 56px); /* NavigationTopBar 높이 제외 */
  background: #ffffff;
  align-items: center;
  justify-content: center;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 100%;
  padding: 24px;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  width: 320px;
  max-width: 100%;
`;

const ChannelList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 4px; /* 스크롤바 공간 확보 */

  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d0d0d0;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #b0b0b0;
  }
`;

const LoadingText = styled.p`
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.regular};
  color: #8f8f8f;
  text-align: center;
`;

const EmptyMessage = styled.p`
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.regular};
  color: #8f8f8f;
  text-align: center;
  padding: 24px;
`;

const ErrorMessage = styled.div`
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${color.palette_status_red_tint_04};
  color: ${color.palette_status_red_100};
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.regular};
  text-align: center;
`;

const SubmitButton = styled.button<{ disabled: boolean }>`
  width: 100%;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.medium};
  background: ${({ disabled }) => (disabled ? '#e8e8e8' : '#7800ff')};
  color: ${({ disabled }) => (disabled ? '#adadad' : '#ffffff')};
  border: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: opacity 0.2s;

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 1 : 0.9)};
  }
`;
